
import React, { useState } from 'react';
import { UserProfile, Language, Theme, DailyTask, SubjectType, ChatMessage } from '../../types';
import { Header } from '../Header';
import { ParentDashboard } from '../../features/ParentDashboard';
import { FocusStation } from '../../features/FocusStation';
import { StudentProfile } from '../../features/StudentProfile';
import { CompanionStatus } from '../../features/CompanionStatus';
import { CompanionAvatar } from '../Companion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { translations } from '../../i18n';
import { chatWithNono } from '../../services/geminiService';

interface GlobalLayoutProps {
  children: React.ReactNode;
  user: UserProfile;
  lang: Language;
  theme: Theme;
  onToggleLang: () => void;
  onToggleTheme: () => void;
  onHome: () => void;
  // Focus Logic
  dailyTasks: DailyTask[];
  onAddTask: (s: SubjectType) => void;
  onRemoveTask: (id: string) => void;
  onToggleTask: (id: string) => void;
  onEnergyCharge: (amount: number) => void;
}

export const GlobalLayout: React.FC<GlobalLayoutProps> = ({ 
  children, user, lang, theme, onToggleLang, onToggleTheme, onHome,
  dailyTasks, onAddTask, onRemoveTask, onToggleTask, onEnergyCharge
}) => {
  const t = translations[lang];
  
  // Local Modal State
  const [showParent, setShowParent] = useState(false);
  const [showFocus, setShowFocus] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCompanion, setShowCompanion] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Chat State
  const [chatHistory, setChatHistory] = useState<{role: 'user'|'model', text: string}[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const handleChat = async () => {
    if (!chatInput.trim() || !user) return;
    const msg = chatInput;
    setChatInput("");
    setChatHistory(h => [...h, { role: 'user', text: msg }]);
    setChatLoading(true);
    const reply = await chatWithNono(chatHistory, msg, user, lang);
    setChatHistory(h => [...h, { role: 'model', text: reply }]);
    setChatLoading(false);
  };

  return (
    <div className={`
      min-h-screen font-sans selection:bg-indigo-500/30 transition-colors duration-500
      ${theme === 'light' ? 'bg-slate-50 text-slate-900' : 'bg-slate-950 text-slate-100'}
    `}>
      {/* 1. Dynamic Background */}
      <div className="fixed inset-0 z-0 pointer-events-none transition-opacity duration-700">
         {theme === 'light' ? (
           <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-cyan-50"></div>
         ) : (
           <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,_#1E1B4B_0%,_#0F172A_40%,_#020617_100%)]"></div>
         )}
      </div>

      {/* 2. Global Header */}
      <Header 
        user={user} 
        lang={lang} 
        theme={theme}
        onToggleLang={onToggleLang}
        onToggleTheme={onToggleTheme}
        onOpenParent={() => setShowParent(true)} 
        onOpenFocus={() => setShowFocus(true)}
        onOpenProfile={() => setShowProfile(true)}
        onOpenCompanion={() => setShowCompanion(true)} 
        onHome={onHome}
        isPro={false} 
      />

      {/* 3. Main Content Injection */}
      <main className="relative z-10 max-w-6xl mx-auto p-4 md:p-8 min-h-[calc(100vh-100px)]">
        {children}
      </main>

      {/* 4. Global Overlays & Modals */}
      
      {/* Chat FAB */}
      <button 
        onClick={() => setShowChat(!showChat)} 
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-indigo-500 to-violet-600 text-white rounded-full shadow-[0_0_30px_rgba(99,102,241,0.6)] flex items-center justify-center z-50 transition-all active:scale-90 hover:rotate-3 border border-white/20"
      >
         {showChat ? <X size={28} /> : <MessageCircle size={28} />}
      </button>

      {/* Chat Window */}
      {showChat && (
        <div className="fixed bottom-24 right-6 w-[360px] h-[520px] bg-white dark:bg-slate-900/95 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-slate-200 dark:border-white/10 z-50 flex flex-col overflow-hidden animate-in zoom-in-95 origin-bottom-right duration-300">
           <div className="bg-indigo-50 dark:bg-indigo-600/30 p-5 text-slate-800 dark:text-white font-bold flex items-center justify-between border-b border-slate-200 dark:border-white/5">
             <div className="flex items-center gap-3">
               <div className="bg-white/50 dark:bg-white/10 p-1 rounded-xl border border-slate-200 dark:border-white/10">
                 <CompanionAvatar level={user.level} size="sm" />
               </div>
               <div>
                 <p className="leading-none text-base">Nono</p>
                 <p className="text-[10px] opacity-70 font-black tracking-widest uppercase text-indigo-500 dark:text-indigo-300">{t.pilotMode}</p>
               </div>
             </div>
             <button onClick={() => setShowChat(false)} className="hover:bg-slate-200 dark:hover:bg-white/10 p-1 rounded-lg transition-colors"><X size={20}/></button>
           </div>
           <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-hide">
              <div className="text-center py-2 mb-4">
                 <span className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-widest bg-indigo-50 dark:bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-100 dark:border-indigo-500/20">{t.warpEncrypted}</span>
              </div>
              {chatHistory.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`}>
                   <div className={`p-4 rounded-2xl max-w-[85%] text-sm leading-relaxed shadow-sm ${m.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 rounded-tl-none'}`}>
                     {m.text}
                   </div>
                </div>
              ))}
              {chatLoading && <div className="text-xs text-slate-400 p-2 italic flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> {t.thinking}
              </div>}
           </div>
           <div className="p-4 border-t border-slate-200 dark:border-white/5 flex gap-2">
             <input 
               className="flex-1 bg-slate-50 dark:bg-white/5 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
               placeholder={t.chatPlaceholder}
               value={chatInput}
               onChange={e => setChatInput(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && handleChat()}
             />
             <button onClick={handleChat} className="bg-indigo-500 text-white p-3 rounded-2xl shadow-[0_0_15px_rgba(99,102,241,0.4)] hover:scale-105 active:scale-95 transition-all">
               <Send size={20}/>
             </button>
           </div>
        </div>
      )}

      {/* Modals */}
      {showParent && <ParentDashboard user={user} lang={lang} onClose={() => setShowParent(false)} />}
      {showProfile && <StudentProfile user={user} lang={lang} theme={theme} onClose={() => setShowProfile(false)} />}
      {showCompanion && <CompanionStatus user={user} lang={lang} theme={theme} onClose={() => setShowCompanion(false)} />}
      
      {showFocus && (
        <FocusStation 
           onComplete={(energy) => { onEnergyCharge(energy); setShowFocus(false); }} 
           onCancel={() => setShowFocus(false)}
           lang={lang}
           theme={theme}
           tasks={dailyTasks}
           onAddTask={onAddTask}
           onRemoveTask={onRemoveTask}
           onToggleTask={onToggleTask}
        />
      )}
    </div>
  );
};
